import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircle, Plus, Trash2, TrendingUp, X } from 'lucide-react';
import { Skeleton } from '../ui/kit';
import { datumUndZeit, uhrzeit, wandzeitOderInstantAlsDatum } from '../lib/zeit';

// Rabatt-Ablauf hat ZWEI Quellen: Blitz-Rabatte kommen als ISO mit Z vom Server; von Hand angelegte
// Rabatte sind Owner-WANDZEIT aus dem datetime-local-Feld (ohne Zone, so gespeichert wie eingegeben).
// Die Wandzeit darf KEIN Z bekommen, sonst läge sie 1–2 h daneben — deshalb wird hier nur das
// Lesen unterschieden; die Anzeige läuft in beiden Fällen über lib/zeit (Europe/Berlin).
const ablaufZeitpunkt = (value) => wandzeitOderInstantAlsDatum(value);

// ─────────────────────────────────────────────────────────────────────────────
// Rabatte & Angebote — EIGENSTÄNDIGE Komponente (aus Settings.js herausgelöst, 2026-07-02).
// Läuft an ZWEI Orten mit identischer Funktion: Einstellungen → Reiter „Rabatte"
// UND Engine → Reiter „Rabatte" (Owner-Wunsch: neben Servicegebiet + Erklärung).
// Enthält: Programme (Einladen/Treuekarte, Wachstum) + Promo-Codes/Auto-Rabatte (CRUD).
// ─────────────────────────────────────────────────────────────────────────────

const inp = { width: '100%', padding: '9px 12px', background: '#e5e7eb', border: '1.5px solid #d1d5db', borderRadius: 9, color: '#0a0a0a', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const smallInp = { ...inp, padding: '7px 10px', fontSize: 12 };
const btn = (color = '#0a0a0a', extra = {}) => ({ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: color, color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', ...extra });
const cardStyle = { background: '#ffffff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 24, marginBottom: 20 };

function SectionHead({ icon: Icon, color, title, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: '#4b5563' }}>{sub}</div>}
      </div>
    </div>
  );
}

// Wachstums-KPIs (Kunde): funktionieren die Programme? Aktivierung D7, Wiederkauf ≤14 Tage,
// entgangener Take je Neukunde, Suchen ohne Fahrer je PLZ (GET /admin/growth-kpis, 90-Tage-Fenster).
function GrowthKpis() {
  const [kpi, setKpi] = useState(null);
  useEffect(() => { api.get('/admin/growth-kpis').then(r => setKpi(r.data)).catch(() => {}); }, []);
  if (!kpi) return null;
  const tile = (label, value, sub) => (
    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: 22, fontWeight: 900, color: '#0a0a0a' }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#4b5563' }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280' }}>{sub}</div>}
    </div>
  );
  const pct = (o) => (o.pct == null ? '–' : `${o.pct} %`);
  const eur = (v) => `${(v || 0).toFixed(2).replace('.', ',')} €`;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        {tile('Aktivierung D7', pct(kpi.activation_d7), `${kpi.activation_d7.activated}/${kpi.activation_d7.total} Neukunden (${kpi.window_days} Tage)`)}
        {tile('Wiederkauf ≤ 14 Tage', pct(kpi.repeat_14d), `${kpi.repeat_14d.repeated}/${kpi.repeat_14d.total} Erstkunden`)}
        {tile('Entgangener Take je Neukunde', kpi.forgone.per_newcomer != null ? eur(kpi.forgone.per_newcomer) : '–', `${kpi.forgone.newcomers} Neukunden · gesamt ${eur(kpi.forgone.take_total)} + ${eur(kpi.forgone.credit_total)} Guthaben`)}
        {tile('Suchen ohne Fahrer (30 T.)', kpi.no_driver_by_plz.reduce((s, r) => s + r.n, 0), kpi.no_driver_by_plz.slice(0, 3).map(r => `${r.plz}: ${r.n}`).join(' · ') || 'keine')}
      </div>
    </div>
  );
}

// ── BLITZ-RABATT-001 (Owner 11.08.2026, Vorbild „Flash-Aktion"): EIN Schalter, direkt an/aus,
// mit Zeitbegrenzung. Fachlich ein normaler automatischer %-Rabatt — hier nur die eine Bühne:
// Prozent + Deckel + Dauer wählen, EIN drücken; AUS wirkt sofort. Die Kunden-App zeigt den
// Blitz in der Klassen-Auswahl (Banner + durchgestrichene Preise + „läuft um … ab").
export function FlashDiscountCard() {
  const [flash, setFlash] = useState(null);
  const [mode, setMode] = useState('fixed');
  const [minPercent, setMinPercent] = useState(2);
  const [maxPercent, setMaxPercent] = useState(15);
  const [minKm, setMinKm] = useState(0);
  const [percent, setPercent] = useState(15);
  const [cap, setCap] = useState(7);
  const [minutes, setMinutes] = useState(60);
  const [cities, setCities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sendPush, setSendPush] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const request = useRef(null);
  const uncertain = useRef(false);
  const sending = useRef(false);
  const initialized = useRef(false);
  const load = () => api.get('/admin/flash-discount').then(r => {
    const f = r.data.flash;
    setFlash(f);
    if (!initialized.current) {
      initialized.current = true;
      if (f) {
        setMode(f.mode || 'fixed'); setPercent(f.percent);
        setMinPercent(f.percent_min ?? f.percent); setMaxPercent(f.percent_max ?? f.percent);
        setMinKm(f.min_distance_km || 0); setCap(f.cap_eur ?? 0);
        if (f.minutes != null) setMinutes(f.minutes);
      }
    }
  }).catch(() => setError('Status konnte nicht geladen werden. Bitte Seite neu laden.'));
  useEffect(() => {
    load();
    api.get('/admin/flash-discount/push-cities').then(r => setCities(r.data.cities || []))
      .catch(() => setError('Städte konnten nicht geladen werden. Bitte Seite neu laden.'));
    const t = setInterval(load, 30_000); return () => clearInterval(t);
  }, []);
  const on = async () => {
    if (sending.current || (sendPush && !selected.length)) return;
    sending.current = true; setBusy(true); setError('');
    try {
      if (!request.current) request.current = { mode, ...(mode === 'random' ? {percent_min:minPercent,percent_max:maxPercent} : {percent}),
        min_distance_km:minKm, cap_eur:cap, minutes, send_push:sendPush,
        ...(sendPush ? {push_cities:selected} : {}), request_id:window.crypto.randomUUID() };
      setPending(true);
      const { data } = await api.post('/admin/flash-discount', request.current);
      if (!data.flash?.id || !Number.isInteger(data.flash.push_queued)) throw new Error('missing activation receipt');
      setFlash(data.flash); setPending(false); request.current=null; uncertain.current=false;
      setResult(`${requestModeLabel(data.flash)}${data.flash.active ? '' : ' Diese Aktion ist bereits beendet.'}`);
      toast.success(data.flash.active ? 'Blitzrabatt aktiviert.' : 'Aktivierung geprüft: Aktion bereits beendet.');
    } catch (e) {
      // A first rejection can be edited; a later 4xx cannot undo an uncertain send.
      const status=e.response?.status;
      if (status>=400 && status<500 && !uncertain.current) { request.current=null; setPending(false); }
      else uncertain.current=true;
      setError(e.response?.data?.error || 'Ergebnis unklar. Mit „Aktivierung prüfen“ dieselbe Anfrage wiederholen.');
    } finally { sending.current=false; setBusy(false); }
  };
  const off = async () => {
    if (sending.current) return;
    sending.current=true; setBusy(true);
    try { await api.post('/admin/flash-discount/off'); toast.success('Blitzrabatt ausgeschaltet.'); await load(); }
    catch { setError('Ausschalten unklar. Bitte Status aktualisieren.'); }
    finally { sending.current=false; setBusy(false); }
  };
  const requestModeLabel = f => !f.push_requested ? 'Blitzrabatt ohne Push aktiviert.'
    : f.push_queued ? `${f.push_queued} Push-Benachrichtigungen vorgemerkt (noch nicht zugestellt).`
      : 'Blitzrabatt aktiviert. Keine berechtigten Empfänger für den Push.';
  const live = flash?.active;
  const locked = busy || pending;
  const until = live ? uhrzeit(ablaufZeitpunkt(flash.expires_at)) : null;
  return (
    <section style={{ ...cardStyle, borderColor:'#9c27b0' }} aria-label="Blitzrabatt">
      <h3>⚡ Blitzrabatt</h3>
      {live && <p>AKTIV: {flash.mode === 'random' ? `${flash.percent_min}–${flash.percent_max}` : `−${flash.percent}`} % · ab {flash.min_distance_km || 0} km · höchstens {flash.cap_eur == null ? 'unbegrenzt' : `${flash.cap_eur} €`}/Fahrt · bis {until} Uhr
        <button style={btn()} disabled={busy} onClick={off}>Ausschalten</button></p>}
      {(!live || pending) && <>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <label>Modus<select aria-label="Modus" style={smallInp} disabled={locked} value={mode} onChange={e=>setMode(e.target.value)}><option value="fixed">Fester Prozentsatz</option><option value="random">Zufälliger Prozentsatz</option></select></label>
          {mode === 'random' ? <>
            <label>Prozent von<input aria-label="Prozent von" style={smallInp} type="number" min="1" max="50" step="1" disabled={locked} value={minPercent} onChange={e=>setMinPercent(Number(e.target.value))} /></label>
            <label>Prozent bis<input aria-label="Prozent bis" style={smallInp} type="number" min={minPercent} max="50" step="1" disabled={locked} value={maxPercent} onChange={e=>setMaxPercent(Number(e.target.value))} /></label>
          </> : <label>Prozent<input aria-label="Prozent" style={smallInp} type="number" min="1" max="50" disabled={locked} value={percent} onChange={e=>setPercent(Number(e.target.value))} /></label>}
          <label>Mindeststrecke (km)<input aria-label="Mindeststrecke (km)" style={smallInp} type="number" min="0" max="1000" step="0.001" disabled={locked} value={minKm} onChange={e=>setMinKm(Number(e.target.value))} /></label>
          <label>Höchstens €/Fahrt<input aria-label="Höchstens €/Fahrt" style={smallInp} type="number" min="0" max="100" step="0.01" disabled={locked} value={cap} onChange={e=>setCap(Number(e.target.value))} /></label>
          <label>Dauer (Minuten)<input aria-label="Dauer" style={smallInp} type="number" min="5" max="720" step="1" disabled={locked} value={minutes} onChange={e=>setMinutes(Number(e.target.value))} /></label>
        </div>
        <label style={{ display:'block', marginTop:16 }}><input type="checkbox" checked={sendPush} disabled={locked}
          onChange={e=>setSendPush(e.target.checked)} /> Push-Benachrichtigung senden</label>
        {sendPush && <>
        <fieldset disabled={locked} style={{ margin:'16px 0', border:'1px solid #d1d5db', borderRadius:8 }}>
          <legend>Empfänger-Städte für den Push</legend>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
            {cities.map(city=><label key={city.id}><input type="checkbox" checked={selected.includes(city.id)}
              onChange={e=>setSelected(e.target.checked ? [...selected,city.id] : selected.filter(c=>c!==city.id))} /> {city.label}</label>)}
          </div>
        </fieldset>
        <p>Nur Kunden mit Werbeeinwilligung und einem in den letzten 15 Minuten gemeldeten, geeigneten Standort in diesen Städten. Die Städteauswahl begrenzt den Push; der Rabatt gilt weiterhin im bisherigen Servicegebiet.</p>
        <p><strong>Push-Vorschau:</strong> ⚡ Blitzrabatt: {mode === 'random' ? `${minPercent}–${maxPercent}` : percent} % ·
          {Number(minKm) > 0 ? ` ab ${Number(minKm).toLocaleString('de-DE')} km ·` : ''} maximal {Number(cap).toLocaleString('de-DE')} € je Fahrt. Die Nachricht enthält die genaue Ablaufzeit.</p>
        </>}
        {mode === 'random' && <p>Der individuelle Prozentsatz wird erst beim Angebot verbindlich zugeteilt. Jede berechtigte Fahrt erhält höchstens den eingestellten Euro-Betrag.</p>}
        <button style={btn('#9c27b0')} disabled={busy || (sendPush && (!selected.length || !cities.length))} onClick={on}>
          {busy ? 'Wird verarbeitet …' : pending ? 'Aktivierung prüfen' : sendPush ? 'Aktivieren und Push vormerken' : 'Ohne Push aktivieren'}
        </button>
      </>}
      {result && <p role="status">{result}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}

export default function DiscountsPanel() {
  const [discounts, setDiscounts] = useState([]);
  const [discLoading, setDiscLoading] = useState(true);
  const [showNewDisc, setShowNewDisc] = useState(false);
  const [newDisc, setNewDisc] = useState({ code: '', title: '', description: '', type: 'percentage', value: '', max_discount: '', min_fare: '0', max_uses: '', per_customer_limit: '1', expires_at: '', show_to_customers: true, trigger_type: 'code', trigger_value: '' });

  // Programme (Wachstum): Einladen + Treuekarte — Schalter & Parameter (/admin/growth-config).
  const [growthCfg, setGrowthCfg] = useState(null);
  const [growthDraft, setGrowthDraft] = useState({ bonus: '', referredBonus: '', monthCap: '', openMax: '', target: '' });

  // VOOGO Miles (Kilometer-Cashback) — Schalter, Parameter, Trockenlauf/Test (/admin/miles-config).
  const [milesCfg, setMilesCfg] = useState(null);
  const [milesDraft, setMilesDraft] = useState({ kmPerEuro: '', minKm: '', capEuro: '', startMonth: '' });
  const [milesRun, setMilesRun] = useState(null);
  const [milesBusy, setMilesBusy] = useState(false);

  const loadGrowth = () => api.get('/admin/growth-config').then(r => {
    setGrowthCfg(r.data);
    setGrowthDraft({
      bonus: (r.data.referral_bonus_cents / 100).toFixed(2),
      referredBonus: ((r.data.referral_referred_bonus_cents ?? r.data.referral_bonus_cents) / 100).toFixed(2),
      monthCap: String(r.data.referral_month_cap),
      openMax: String(r.data.referral_max_open_invites),
      target: String(r.data.loyalty_target),
    });
  }).catch(() => {});

  const saveGrowth = async (updates) => {
    try {
      await api.post('/admin/growth-config', updates);
      await loadGrowth();
      toast.success('Übernommen');
    } catch { toast.error('Fehler'); }
  };

  const loadMiles = () => api.get('/admin/miles-config').then(r => {
    setMilesCfg(r.data);
    setMilesDraft({
      kmPerEuro: String(r.data.miles_km_per_euro),
      minKm: String(r.data.miles_min_km),
      capEuro: (r.data.miles_monthly_cap_cents / 100).toFixed(2),
      startMonth: r.data.miles_start_month || '',
    });
  }).catch(() => {});

  const saveMiles = async (updates) => {
    try { await api.post('/admin/miles-config', updates); await loadMiles(); toast.success('Übernommen'); }
    catch { toast.error('Fehler'); }
  };

  // „Jetzt rechnen": rechnet den Monatsabschluss auf Knopfdruck (respektiert den Trockenlauf-Schalter).
  const runMiles = async () => {
    setMilesBusy(true);
    try { const r = await api.post('/admin/miles-run'); setMilesRun(r.data.summary); await loadMiles(); toast.success('Berechnet'); }
    catch (e) { toast.error(e.response?.data?.error || 'Fehler'); }
    setMilesBusy(false);
  };

  useEffect(() => {
    api.get('/admin/discounts').then(r => { setDiscounts(r.data.discounts); setDiscLoading(false); }).catch(() => setDiscLoading(false));
    loadGrowth();
    loadMiles();
  }, []);

  const createDiscount = async () => {
    if (!newDisc.title || !newDisc.value) return toast.error('Titel und Wert angeben');
    if (newDisc.trigger_type === 'code' && !newDisc.code) return toast.error('Code-Rabatt braucht einen Code');
    if (['nth_ride', 'min_distance'].includes(newDisc.trigger_type) && !newDisc.trigger_value) return toast.error('Auslöser-Wert angeben');
    try {
      const r = await api.post('/admin/discounts', {
        ...newDisc,
        value: parseFloat(newDisc.value),
        max_discount: newDisc.max_discount !== '' ? parseFloat(newDisc.max_discount) : null,
        min_fare: parseFloat(newDisc.min_fare || 0),
        max_uses: newDisc.max_uses ? parseInt(newDisc.max_uses) : null,
        per_customer_limit: newDisc.per_customer_limit !== '' ? parseInt(newDisc.per_customer_limit) : 1,
        trigger_value: newDisc.trigger_value !== '' ? parseFloat(newDisc.trigger_value) : null,
      });
      setDiscounts(prev => [r.data.discount, ...prev]);
      setShowNewDisc(false);
      setNewDisc({ code: '', title: '', description: '', type: 'percentage', value: '', max_discount: '', min_fare: '0', max_uses: '', per_customer_limit: '1', expires_at: '', show_to_customers: true, trigger_type: 'code', trigger_value: '' });
      toast.success('Rabatt erstellt');
    } catch (e) { toast.error(e.response?.data?.error || 'Fehler'); }
  };

  const toggleDiscount = async (id, is_active) => {
    try {
      const r = await api.patch(`/admin/discounts/${id}`, { is_active: is_active ? 1 : 0 });
      setDiscounts(prev => prev.map(d => d.id === id ? r.data.discount : d));
    } catch { toast.error('Fehler'); }
  };

  const deleteDiscount = async (id) => {
    if (!window.confirm('Rabatt löschen?')) return;
    try {
      await api.delete(`/admin/discounts/${id}`);
      setDiscounts(prev => prev.filter(d => d.id !== id));
      toast.success('Gelöscht');
    } catch { toast.error('Fehler'); }
  };

  return (
    <div style={cardStyle}>
      <SectionHead icon={TrendingUp} color="#9c27b0" title="Rabatte & Angebote" sub="Promo-Codes und Kundenangebote" />

      <GrowthKpis />

      <FlashDiscountCard />

      {/* Programme (Wachstum): Einladen + Treuekarte — steuern den „Vorteile"-Tab der Kunden-App.
          Finanzierung: zuerst der VOOGO-Anteil, ein Rest über den Fahrpreis (Abrechnung). */}
      <div style={{ background: '#e5e7eb', borderRadius: 12, padding: 18, marginBottom: 20, border: '1px solid #9c27b033' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#9c27b0', marginBottom: 4 }}>Programme — Kunden-App „Vorteile"</div>
        <p style={{ fontSize: 12, color: '#4b5563', marginBottom: 12 }}>
          Der Vorteile-Tab (Guthaben, Treuekarte, Einladen) ist in der App immer sichtbar; Stempel zählen immer mit.
          Diese Schalter machen die Programme <strong>scharf</strong>: Bonusfahrt-Freischaltung und Einladungs-Prämien fließen nur bei AN.
        </p>
        {growthCfg ? (() => {
          const lbl = { fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 };
          const onOff = (active, onClick) => (
            <button onClick={onClick} style={{ border: 'none', cursor: 'pointer', borderRadius: 999, padding: '4px 14px', fontSize: 12, fontWeight: 800, color: '#fff', background: active ? '#16a34a' : '#6b7280' }}>
              {active ? 'AN' : 'AUS'}
            </button>
          );
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>Einladen (Referral)</div>
                  {onOff(growthCfg.referral_active, () => saveGrowth({ referral_active: !growthCfg.referral_active }))}
                </div>
                <p style={{ fontSize: 12, color: '#4b5563', marginBottom: 10 }}>
                  Gezielt per Handynummer (Code gilt nur für diese Nummer, jede Nummer 1×). Werber und Geworbener bekommen ihr getrennt konfigurierbares Fahrt-Guthaben gemeinsam nach der abgeschlossenen Erstfahrt — beide oder keiner.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, marginBottom: 10 }}>
                  <div><label style={lbl}>Werber-Prämie (€)</label>
                    <input type="number" step="0.5" value={growthDraft.bonus} onChange={e => setGrowthDraft(p => ({ ...p, bonus: e.target.value }))} style={smallInp} /></div>
                  <div><label style={lbl}>Geworbenen-Prämie (€)</label>
                    <input type="number" step="0.5" value={growthDraft.referredBonus} onChange={e => setGrowthDraft(p => ({ ...p, referredBonus: e.target.value }))} style={smallInp} /></div>
                  <div><label style={lbl}>Deckel / Monat</label>
                    <input type="number" value={growthDraft.monthCap} onChange={e => setGrowthDraft(p => ({ ...p, monthCap: e.target.value }))} style={smallInp} /></div>
                  <div><label style={lbl}>Offene Einl. max</label>
                    <input type="number" value={growthDraft.openMax} onChange={e => setGrowthDraft(p => ({ ...p, openMax: e.target.value }))} style={smallInp} /></div>
                </div>
                <button onClick={() => saveGrowth({
                  referral_bonus_cents: Math.round(parseFloat(String(growthDraft.bonus).replace(',', '.')) * 100),
                  referral_referred_bonus_cents: Math.round(parseFloat(String(growthDraft.referredBonus).replace(',', '.')) * 100),
                  referral_month_cap: parseInt(growthDraft.monthCap, 10),
                  referral_max_open_invites: parseInt(growthDraft.openMax, 10),
                })} style={btn('#9c27b0')}>Speichern</button>
              </div>
              <div style={{ background: '#fff', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>Treuekarte (Stempel)</div>
                  {onOff(growthCfg.loyalty_active, () => saveGrowth({ loyalty_active: !growthCfg.loyalty_active }))}
                </div>
                <p style={{ fontSize: 12, color: '#4b5563', marginBottom: 10 }}>
                  Jede abgeschlossene Fahrt = 1 Stempel. Volle Karte schaltet eine Bonusfahrt frei; der Kunde aktiviert sie selbst.
                  <strong> Prozentsatz + Deckel der Bonusfahrt:</strong> Rabatt-Zeile „Bonusfahrt (Treuekarte)" in der Liste unten.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, marginBottom: 10 }}>
                  <div><label style={lbl}>Ziel-Stempel</label>
                    <input type="number" value={growthDraft.target} onChange={e => setGrowthDraft(p => ({ ...p, target: e.target.value }))} style={smallInp} /></div>
                </div>
                <button onClick={() => saveGrowth({ loyalty_target: parseInt(growthDraft.target, 10) })} style={btn('#9c27b0')}>Speichern</button>
              </div>
            </div>
          );
        })() : <p style={{ fontSize: 12, color: '#6b7280' }}>lädt…</p>}
      </div>

      {/* VOOGO Miles — distanz-basiertes Monats-Cashback als Guthaben (take-finanziert, kein Bargeld). */}
      <div style={{ background: '#e5e7eb', borderRadius: 12, padding: 18, marginBottom: 20, border: '1px solid #9c27b033' }}>
        {(() => {
          const lbl = { fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 };
          const onOff = (active, onClick) => (
            <button onClick={onClick} style={{ border: 'none', cursor: 'pointer', borderRadius: 999, padding: '4px 14px', fontSize: 12, fontWeight: 800, color: '#fff', background: active ? '#16a34a' : '#6b7280' }}>
              {active ? 'AN' : 'AUS'}
            </button>
          );
          return (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#9c27b0' }}>VOOGO Miles — Kilometer-Cashback</div>
                {milesCfg && onOff(milesCfg.miles_active, () => saveMiles({ miles_active: !milesCfg.miles_active }))}
              </div>
              <p style={{ fontSize: 12, color: '#4b5563', marginBottom: 12 }}>
                Kunden sammeln für abgeschlossene Fahrten Kilometer und lösen sie in der App per Knopf als Guthaben ein
                (take-finanziert, kein Bargeld). Am Monatsanfang schreibt VOOGO das noch offene Guthaben automatisch gut.
                Der obere Schalter zeigt/versteckt Miles in der Kunden-App.
              </p>
              {milesCfg ? (
                <div style={{ background: '#fff', borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800 }}>Automatische Monats-Auszahlung</div>
                      <div style={{ fontSize: 12, color: '#4b5563' }}>AN: schreibt am Monatsanfang jedem das noch offene Guthaben automatisch gut. AUS: automatische Monatsgutschrift pausiert; „Jetzt rechnen" zeigt nur die Berechnung und schreibt nichts gut. Der Einlösen-Knopf in der App funktioniert unabhängig davon.</div>
                    </div>
                    {onOff(!milesCfg.miles_dry_run, () => saveMiles({ miles_dry_run: !milesCfg.miles_dry_run }))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 10 }}>
                    <div><label style={lbl}>km je 1 €</label>
                      <input type="number" value={milesDraft.kmPerEuro} onChange={e => setMilesDraft(p => ({ ...p, kmPerEuro: e.target.value }))} style={smallInp} /></div>
                    <div><label style={lbl}>ab … km/Monat</label>
                      <input type="number" value={milesDraft.minKm} onChange={e => setMilesDraft(p => ({ ...p, minKm: e.target.value }))} style={smallInp} /></div>
                    <div><label style={lbl}>Deckel €/Monat</label>
                      <input type="number" step="0.5" value={milesDraft.capEuro} onChange={e => setMilesDraft(p => ({ ...p, capEuro: e.target.value }))} style={smallInp} /></div>
                    <div><label style={lbl}>Ab Monat (JJJJ-MM)</label>
                      <input placeholder="ab sofort" value={milesDraft.startMonth} onChange={e => setMilesDraft(p => ({ ...p, startMonth: e.target.value }))} style={smallInp} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => saveMiles({
                      miles_km_per_euro: parseInt(milesDraft.kmPerEuro, 10),
                      miles_min_km: parseInt(milesDraft.minKm, 10),
                      miles_monthly_cap_cents: Math.round(parseFloat(String(milesDraft.capEuro).replace(',', '.')) * 100),
                      miles_start_month: String(milesDraft.startMonth).trim(),
                    })} style={btn('#9c27b0')}>Speichern</button>
                    <button onClick={runMiles} disabled={milesBusy} style={btn('#374151')}>{milesBusy ? 'Rechnet…' : 'Vorschau berechnen'}</button>
                  </div>
                  {(milesRun || milesCfg.last_run) && (() => {
                    const s = milesRun || milesCfg.last_run;
                    return (
                      <div style={{ marginTop: 12, fontSize: 12, color: '#374151', background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
                        <strong>Letzter Lauf</strong> — Monat {s.month}, {s.dry_run ? 'Trockenlauf' : 'echt'}: {s.customers} Kunden,{' '}
                        {(((s.credited_cents || 0) / 100).toFixed(2)).replace('.', ',')} € {s.dry_run ? 'projiziert' : 'gutgeschrieben'}{' '}
                        ({s.km_total} km · {s.capped} gedeckelt · {s.skipped_min} unter Mindest-km).
                        {s.note && <div style={{ color: '#6b7280', marginTop: 4 }}>{s.note}</div>}
                      </div>
                    );
                  })()}
                </div>
              ) : <p style={{ fontSize: 12, color: '#6b7280' }}>lädt…</p>}
            </>
          );
        })()}
      </div>

      <button onClick={() => setShowNewDisc(!showNewDisc)} style={{ ...btn('#9c27b0'), marginBottom: 20 }}>
        <Plus size={15} /> Neues Angebot
      </button>

      {showNewDisc && (
        <div style={{ background: '#e5e7eb', borderRadius: 12, padding: 18, marginBottom: 20, border: '1px solid #9c27b033' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#9c27b0', marginBottom: 14 }}>Neuer Rabatt / Angebot</div>
          {(() => { const lbl = { fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }; const isCode = newDisc.trigger_type === 'code'; return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            {/* Auslöser bestimmt Code vs. automatisch */}
            <div>
              <label style={lbl}>Auslöser</label>
              {/* RESERVIERUNG-RABATT-001: Reservierung startet mit „erste 2 Reservierungen", 2× je Kunde (Owner 14.10.2026). */}
              <select value={newDisc.trigger_type} onChange={e => { const t = e.target.value; setNewDisc(p => ({ ...p, trigger_type: t, ...(t === 'reservation' && p.trigger_type !== 'reservation' ? { trigger_value: '2', per_customer_limit: '2' } : {}) })); }} style={smallInp}>
                <option value="code">Gutschein-Code (Kunde gibt ein)</option>
                <option value="new_customer">Automatisch · Neukunde (1. Fahrt)</option>
                <option value="nth_ride">Automatisch · N-te Fahrt</option>
                <option value="min_distance">Automatisch · Lange Fahrt (ab km)</option>
                <option value="reservation">Reservierungspreis senken · Normal/Ultimate</option>
                <option value="all">Automatisch · Alle Kunden</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Titel *</label>
              <input value={newDisc.title} onChange={e => setNewDisc(p => ({ ...p, title: e.target.value }))} placeholder="z.B. Willkommensangebot" style={smallInp} />
            </div>
            {isCode && (
              <div>
                <label style={lbl}>Code *</label>
                <input value={newDisc.code} onChange={e => setNewDisc(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="WELCOME10" style={smallInp} />
              </div>
            )}
            {newDisc.trigger_type === 'nth_ride' && (
              <div>
                <label style={lbl}>Welche Fahrtnummer?</label>
                <input type="number" value={newDisc.trigger_value} onChange={e => setNewDisc(p => ({ ...p, trigger_value: e.target.value }))} placeholder="z.B. 2 = 2. Fahrt" style={smallInp} />
              </div>
            )}
            {newDisc.trigger_type === 'min_distance' && (
              <div>
                <label style={lbl}>Ab Distanz (km)</label>
                <input type="number" value={newDisc.trigger_value} onChange={e => setNewDisc(p => ({ ...p, trigger_value: e.target.value }))} placeholder="z.B. 20" style={smallInp} />
              </div>
            )}
            {newDisc.trigger_type === 'reservation' && (
              <div>
                <label style={lbl}>Nur die ersten … Reservierungen je Kunde</label>
                <input type="number" min="0" step="1" value={newDisc.trigger_value} onChange={e => setNewDisc(p => ({ ...p, trigger_value: e.target.value, per_customer_limit: e.target.value === '' ? '0' : e.target.value }))} placeholder="leer = jede Reservierung" style={smallInp} />
                <small>Senkt den Verkaufspreis von Normal-/Ultimate-Reservierungen, auch bei Start in Essen. Für Fahrten im Essen-Tarif gilt nur die Neukundenstaffel 15/10/5. Keine VOOGO-Erstattung.</small>
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Beschreibung</label>
              <input value={newDisc.description} onChange={e => setNewDisc(p => ({ ...p, description: e.target.value }))} placeholder="Kurze Info für Kunden" style={smallInp} />
            </div>
            <div>
              <label style={lbl}>Art</label>
              <select value={newDisc.type} onChange={e => setNewDisc(p => ({ ...p, type: e.target.value }))} style={smallInp}>
                <option value="percentage">Prozent (%)</option>
                <option value="fixed">Fester Betrag (€)</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Wert *</label>
              <input type="number" value={newDisc.value} onChange={e => setNewDisc(p => ({ ...p, value: e.target.value }))} placeholder={newDisc.type === 'percentage' ? '10 = 10%' : '5 = 5€'} style={smallInp} />
            </div>
            {newDisc.type === 'percentage' && (
              <div>
                <label style={{ ...lbl, color: '#9c27b0' }}>Max. Rabatt (€)</label>
                <input type="number" value={newDisc.max_discount} onChange={e => setNewDisc(p => ({ ...p, max_discount: e.target.value }))} placeholder="leer = unbegrenzt" style={{ ...smallInp, borderColor: '#9c27b044' }} />
              </div>
            )}
            <div>
              <label style={lbl}>Mindestbetrag (€)</label>
              <input type="number" value={newDisc.min_fare} onChange={e => setNewDisc(p => ({ ...p, min_fare: e.target.value }))} style={smallInp} />
            </div>
            <div>
              <label style={lbl}>Pro Kunde max.</label>
              <input type="number" value={newDisc.per_customer_limit} onChange={e => setNewDisc(p => ({ ...p, per_customer_limit: e.target.value }))} placeholder="1" style={smallInp} />
            </div>
            <div>
              <label style={lbl}>Max. Nutzungen (gesamt)</label>
              <input type="number" value={newDisc.max_uses} onChange={e => setNewDisc(p => ({ ...p, max_uses: e.target.value }))} placeholder="Unbegrenzt" style={smallInp} />
            </div>
            <div>
              <label style={lbl}>Gültig bis</label>
              <input type="datetime-local" value={newDisc.expires_at} onChange={e => setNewDisc(p => ({ ...p, expires_at: e.target.value }))} style={smallInp} />
            </div>
          </div>
          ); })()}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={createDiscount} style={btn('#9c27b0')}><CheckCircle size={14} /> Erstellen</button>
            <button onClick={() => setShowNewDisc(false)} style={btn('#d1d5db')}><X size={14} /> Abbruch</button>
          </div>
        </div>
      )}

      {discLoading && <Skeleton h={120} r={12} />}
      {!discLoading && discounts.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 13 }}>Keine Rabatte vorhanden</div>
      )}

      {discounts.map(d => {
        const ablauf = ablaufZeitpunkt(d.expires_at);
        const expired = Boolean(ablauf && ablauf < new Date());
        const exhausted = d.max_uses && d.used_count >= d.max_uses;
        const inactive = !d.is_active || expired || exhausted;
        return (
          <div key={d.id} style={{ background: '#e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 10, border: `1px solid ${inactive ? '#d1d5db' : '#9c27b033'}`, opacity: inactive ? 0.7 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: inactive ? '#4b5563' : '#0a0a0a' }}>{d.title}</span>
                  {d.code && <span style={{ fontSize: 12, fontWeight: 800, color: '#9c27b0', background: '#9c27b020', padding: '2px 8px', borderRadius: 6, letterSpacing: 0.5 }}>{d.code}</span>}
                  <span style={{ fontSize: 12, fontWeight: 700, color: d.type === 'percentage' ? '#0a0a0a' : '#4b5563', background: d.type === 'percentage' ? '#0a0a0a20' : '#4b556320', padding: '2px 8px', borderRadius: 6 }}>
                    {d.type === 'percentage' ? `−${d.flash_mode === 'random' ? `${d.flash_min_percent}–${d.flash_max_percent}` : d.value}%${d.max_discount != null ? ` (max ${d.max_discount}€)` : ''}` : `−${d.value}€`}
                  </span>
                  {d.trigger_type && d.trigger_type !== 'code' && (
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#4b5563', background: '#4b556318', padding: '2px 8px', borderRadius: 6, letterSpacing: 0.3 }}>
                      {({ new_customer: 'AUTO · NEUKUNDE', nth_ride: `AUTO · ${d.trigger_value}. FAHRT`, min_distance: `AUTO · AB ${d.trigger_value} KM`, reservation: d.trigger_value > 0 ? `AUTO · ERSTE ${d.trigger_value} RESERVIERUNGEN` : 'AUTO · RESERVIERUNGEN', all: 'AUTO · ALLE', loyalty: 'TREUEKARTE' })[d.trigger_type] || 'AUTO'}
                    </span>
                  )}
                  {inactive && <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>{expired ? 'ABGELAUFEN' : exhausted ? 'VERBRAUCHT' : 'INAKTIV'}</span>}
                </div>
                {d.description && <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>{d.description}</div>}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {d.min_fare > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>Mind. {d.min_fare}€</span>}
                  {d.per_customer_limit > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>{d.per_customer_limit}× / Kunde</span>}
                  {d.max_uses && <span style={{ fontSize: 12, color: '#6b7280' }}>{d.used_count}/{d.max_uses} genutzt</span>}
                  {!d.max_uses && <span style={{ fontSize: 12, color: '#6b7280' }}>{d.used_count}× genutzt</span>}
                  {d.expires_at && <span style={{ fontSize: 12, color: expired ? '#ef4444' : '#4b5563' }}>bis {datumUndZeit(ablaufZeitpunkt(d.expires_at), { jahr: 'voll' })}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 10 }}>
                <button disabled={d.flash_mode === 'random'} title={d.flash_mode === 'random' ? 'Über die Blitzrabatt-Einstellungen ändern' : undefined} onClick={() => toggleDiscount(d.id, !d.is_active)}
                  style={{ padding: '5px 10px', borderRadius: 7, border: 'none', background: d.is_active ? '#9c27b020' : '#d1d5db20', color: d.is_active ? '#9c27b0' : '#4b5563', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {d.is_active ? 'Aktiv' : 'Inaktiv'}
                </button>
                <button onClick={() => deleteDiscount(d.id)}
                  style={{ padding: '5px 8px', borderRadius: 7, border: 'none', background: '#ef444420', color: '#ef4444', cursor: 'pointer' }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
